import React, { ReactNode } from "react";

import Icon from "../../../../../shared/components/design-system/Icon";
import InputLabel from "../../../../../shared/components/design-system/InputLabel";
import useGeneratedId from "../../../../../shared/hooks/useGeneratedId";
import styles from "./styles.module.scss";

interface RadioCardOption<T extends string> {
  title: string;
  body?: string;
  id: T;
}

export default function RadioCards<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: ReactNode;
  options: Readonly<RadioCardOption<T>[]>;
  value: T;
  onChange: (value: T) => void;
}) {
  const groupId = useGeneratedId();

  return (
    <fieldset className={styles.RadioCardContainer}>
      <legend className={styles.Hidden}>{label}</legend>
      <div className={styles.Options}>
        {options.map(({ id, title, body }) => (
          <RadioCard
            key={id}
            groupId={groupId}
            checked={id === value}
            onClick={() => onChange(id)}
            title={title}
            body={body}
          />
        ))}
      </div>
    </fieldset>
  );
}

function RadioCard({
  groupId,
  checked,
  title,
  body,
  onClick,
}: {
  groupId?: string;
  checked: boolean;
  title: string;
  body?: string;
  onClick: () => void;
}) {
  const id = useGeneratedId();
  return (
    <span className={styles.RadioCard}>
      <input name={groupId} id={id} type="radio" checked={checked} onChange={onClick} required />

      <InputLabel variant="paragraph" htmlFor={id} className={styles.Label}>
        <span>
          <span className={styles.Title}>
            <p>{title}</p>
          </span>
          {body ? <p className={styles.Body}>{body}</p> : null}
        </span>
        <span>
          {checked ? (
            <Icon className="ds-text-violet-500" size={24} icon="circleCheck" />
          ) : (
            <Icon icon={null} size={24} />
          )}
        </span>
      </InputLabel>
    </span>
  );
}
